/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect, useState } from 'react';
import {
	Box,
	Progress,
	Image,
	rem,
	Text,
} from '@mantine/core';
import classes from './SingleFileUploadWithProgress.module.css';

// Icons
import SheetIcon from '@assets/images/sheet-file-icon.svg';
import CancelUploadIcon from '@assets/images/cancel-upload-icon.svg';
import {
	IconCircleCheckFilled,
	IconTrash,
} from '@tabler/icons-react';

// Utils
import { truncateString } from '@utils/functions';
import { Tooltip } from '@components/atoms/Tooltip';
import { useUploadMedia } from './api/upload-media';
import { apiClient } from '@lib/api-client';
import isNil from 'lodash/isNil';

export interface SingleFileUploadWithProgressProps {
	entity?: string;
	isPublicFile?: boolean;
	file: any;
	onDelete: (file: File) => void;
	onUpload: (file: File, status: any) => void;
	onReload: () => void;
	onGetUploadFileStatus: (status: any) => void;
	isToUpdate: boolean;
	id?: string;
	apiUploadFile: string;
	documentId?: string;
	route?: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const SingleFileUploadWithProgress = ({
	entity,
	isPublicFile,
	file,
	onDelete,
	onUpload,
	onReload,
	onGetUploadFileStatus,
	isToUpdate,
	apiUploadFile,
	documentId,
	route,
}: SingleFileUploadWithProgressProps) => {
	const [progress, setProgress] = useState(0);

	const uploadMedia = (
		data: any
	): Promise<{
		id: any;
		response: any;
	}> => {
		return apiClient.post(apiUploadFile, data);
	};

	const uploadTechnicalDocuments =
		useUploadMedia(uploadMedia);

	function uploadFile(
		onProgress: (percentage: number) => void
	) {
		onProgress(Math.round(100));
	}

	/**
	 * Effect hook to handle the file upload process.
	 *
	 * This hook encapsulates the logic for preparing the file upload
	 * and executing the upload operation. It constructs a FormData
	 * object to hold the necessary data and invokes the upload
	 * function based on the specified conditions.
	 *
	 *
	 * @notes
	 * - This effect runs once on component mount due to the empty dependency array.
	 * - Ensure proper error handling for the upload process to improve robustness.
	 * - Use of `FormData` is crucial for handling file uploads in the context of
	 *   multipart/form-data requests.
	 */
	useEffect(() => {
		async function uploadFileApi() {
			const formData = new FormData();
			if (!isPublicFile && documentId && route) {
				formData.append('document', documentId);
				formData.append('route', route);
			}
			formData.append('file', file);
			if (!isToUpdate) {
				uploadTechnicalDocuments.mutate(formData);
			}
			onUpload(file, uploadTechnicalDocuments.isSuccess);
			await uploadFile(setProgress);
		}

		uploadFileApi();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// isLoading dependency
	useEffect(() => {
		if (uploadTechnicalDocuments) {
			onGetUploadFileStatus({
				[file.path]: uploadTechnicalDocuments,
			});
		}
	}, [uploadTechnicalDocuments.isLoading]);

	useEffect(() => {
		if (uploadTechnicalDocuments) {
			onGetUploadFileStatus({
				[file.path]: uploadTechnicalDocuments,
			});
		}
	}, [uploadTechnicalDocuments.isSuccess]);

	return (
		<Box className={classes.container}>
			<Box className={classes.wrapper}>
				<Image
					style={{
						width: rem(29),
						height: rem(29),
						color: 'var(--mantine-color-dimmed)',
					}}
					src={SheetIcon}
				/>
				<Tooltip
					isActive={
						file && file.path && file.path.length > 20
					}
					label={file && file.path && file.path}
				>
					<Text className={classes.filename}>
						{truncateString(
							file && file.path && file.path,
							20
						)}
					</Text>
				</Tooltip>
				<Box className={classes.containerProgress}>
					<Progress.Root
						size="md"
						className={classes.progressRoot}
					>
						<Progress.Section
							color={
								!uploadTechnicalDocuments.isLoading
									? 'success'
									: 'orange'
							}
							value={progress}
							style={{
								borderRadius: '1px',
							}}
						/>
					</Progress.Root>
					{progress == 100 &&
						!uploadTechnicalDocuments.isLoading && (
							<IconCircleCheckFilled
								style={{
									width: rem(30),
									height: rem(30),
									color: 'var(--mantine-color-success-9)',
								}}
								stroke={1.5}
							/>
						)}
					{progress !== 100 && (
						<Image
							data-testid="D2LtDXR7_Z5jdHi11-button"
							onClick={() => {
								onDelete(file);
							}}
							className={classes.cancelUpload}
							src={CancelUploadIcon}
						/>
					)}
					{progress == 100 &&
						!uploadTechnicalDocuments.isLoading && (
							<IconTrash
								className={classes.reloadRowIcon}
								data-testid="D2EtDXR7_Z5jdHi6B-dropzone"
								onClick={() => {
									onReload();
								}}
							/>
						)}
				</Box>
			</Box>
		</Box>
	);
};

export default SingleFileUploadWithProgress;
